// Shared HypeVault watermark. Renders bottom-right of its nearest positioned
// ancestor (uses position: absolute, NOT fixed) so it lands inside the
// widget's visual container rather than floating in whatever empty space
// happens to be at the viewport corner.
//
// The component is render-only — gating is the caller's job:
//   {streamerTier === 'free' && <Watermark />}
//
// Caller responsibilities:
//   1. Provide a positioned ancestor (`position: relative/absolute/fixed`).
//   2. Put the watermark INSIDE the visible widget chrome, not outside it,
//      so it aligns with the widget's bottom-right corner.
//
// WidgetView wraps non-game widgets in a positioned viewport container; the
// GameWidget places the watermark directly inside its `.gx-card`.

// `variant: 'corner'` (default) — position:absolute bottom-right of nearest
// positioned ancestor. Use inside a `position: relative` widget chrome.
// `variant: 'inline'` — position:static, drops into normal flow as an
// inline-flex pill. Use when you want the watermark to live in a layout row
// rather than overlap content.
// `variant: 'right-edge'` — position:absolute pinned to right edge, vertically
// centered. Use on horizontal strip widgets (activity ticker, etc.) where
// there's no useful "below" position.
export function Watermark({
  variant = 'corner',
}: { variant?: 'corner' | 'inline' | 'right-edge' } = {}) {
  return (
    <>
      <div className={`hv-watermark hv-watermark-${variant}`} aria-hidden>
        <span className="hv-watermark-dot" />
        <span className="hv-watermark-text">
          <span className="hv-watermark-pre">powered by</span>
          <span className="hv-watermark-brand">HypeVault</span>
        </span>
      </div>
      <WatermarkStyles />
    </>
  );
}

function WatermarkStyles() {
  return (
    <style>{`
      .hv-watermark {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 4px 10px;
        border-radius: 999px;
        background: rgba(10, 8, 24, 0.55);
        border: 1px solid rgba(246, 85, 166, 0.35);
        backdrop-filter: blur(8px);
        opacity: 0.78;
        pointer-events: none;
        z-index: 999;
        font-family: 'Inter', system-ui, sans-serif;
        line-height: 1;
      }
      .hv-watermark-corner {
        position: absolute;
        right: 14px;
        bottom: 12px;
      }
      .hv-watermark-inline {
        position: static;
      }
      .hv-watermark-right-edge {
        position: absolute;
        right: 12px;
        top: 50%;
        transform: translateY(-50%);
      }
      .hv-watermark-dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: #f655a6;
        box-shadow: 0 0 8px rgba(246, 85, 166, 0.8);
        flex-shrink: 0;
      }
      .hv-watermark-text {
        display: inline-flex;
        align-items: baseline;
        gap: 4px;
      }
      .hv-watermark-pre {
        font-size: 9px;
        font-weight: 600;
        letter-spacing: 1.3px;
        text-transform: uppercase;
        color: rgba(255, 255, 255, 0.55);
      }
      .hv-watermark-brand {
        font-size: 12px;
        font-weight: 900;
        letter-spacing: 0.2px;
        background: linear-gradient(90deg, #f655a6, #fda4af);
        background-clip: text;
        -webkit-background-clip: text;
        color: transparent;
        text-shadow: 0 0 16px rgba(246, 85, 166, 0.35);
      }
    `}</style>
  );
}
